import type { ExperimentItem } from '@/types';

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RiEdit2Line, RiFileCopyLine, RiCloseCircleLine, RiEyeLine, RiShieldCrossLine, RiShieldCheckLine } from '@remixicon/react';
import { Chip, Tooltip, Button } from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';

import { TablePage, ColumnsToggleDropdown, FilterSelect, FilterMultiSelect, TableFilters } from '@/components/table';
import { experimentsListParamsAtom, experimentsQueryAtom, setExperimentStatus } from '@/store';

// Custom hooks
import { useExperimentsFilters, useExperimentsSorting } from '@/hooks/experiments';
import { useVisibleColumns } from '@/hooks/table';

// Components
import { UserCell } from '@/components/shared/UserCell';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { SearchInput } from '@/components/shared/SearchInput';
import { SetExperimentStatusModal } from '@/components/experiments/SetExperimentStatusModal';
import { BlockExperimentModal } from '@/components/experiments/BlockExperimentModal';
import { UnblockExperimentModal } from '@/components/experiments/UnblockExperimentModal';
import { CancelExperimentModal } from '@/components/experiments/CancelExperimentModal';
import { DomainCell } from '@/components/shared/DomainCell';
import { OrganizationCell } from '@/components/shared/OrganizationCell';
import { ExperimentTypesBadges } from '@/components/experiments/ExperimentTypesBadges';
import { SelectorReadOnlyView } from '@/components/experiments/SelectorReadOnlyView';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { copyToClipboard } from '@/utils/helpers';

// Utils
import { getTrackerLabel } from '@/utils/trackers';
import {
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  getStatusOptions,
  isBlockableStatus,
  isCancellableStatus,
  type ExperimentStatus
} from '@/utils/experiments';

export default function ExperimentsPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useAtomValue(experimentsQueryAtom);
  const [ـ, setParams] = useAtom(experimentsListParamsAtom);
  const [, setStatusUpdateAtom] = useAtom(setExperimentStatus);

  // Modal state
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = React.useState(false);
  const [isUnblockModalOpen, setIsUnblockModalOpen] = React.useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = React.useState(false);
  const [selectedExperiment, setSelectedExperiment] = React.useState<ExperimentItem | null>(null);

  // Custom hooks
  const {
    searchTerm,
    setSearchTerm,
    configSearchTerm,
    setConfigSearchTerm,
    statusFilter,
    typesFilter,
    hasAnyFilter,
    handleSearch,
    handleClearSearch,
    handleConfigSearch,
    handleClearConfigSearch,
    clearFilters,
    handleStatusChange,
    handleTypesChange,
    handlePageChange,
    typeOptions
  } = useExperimentsFilters();

  const experiments = React.useMemo<ExperimentItem[]>(() => data?.data ?? [], [data]);
  const { sortDescriptor, setSortDescriptor, sortedExperiments } = useExperimentsSorting(experiments);

  // Function to trigger data refresh
  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  // Copy unique_id to clipboard
  const copyUniqueId = React.useCallback(async (uniqueId: string, domainName: string) => {
    await copyToClipboard(uniqueId, `Unique ID for ${domainName} copied to clipboard`);
  }, []);

  // Get status indicator color - semantic: Green=Active, Primary=Completed, Orange=Stopped, Red=Cancelled
  const getStatusIndicatorColor = React.useCallback((status: string): string => {
    const colorMap: Record<string, string> = {
      active: 'bg-success',
      running: 'bg-success',
      draft: 'bg-default-400',
      completed: 'bg-primary',
      finished: 'bg-primary',
      blocked: 'bg-warning',
      cancelled: 'bg-danger'
    };
    return colorMap[status] || 'bg-default-400';
  }, []);

  const handleStatusClick = React.useCallback((experiment: ExperimentItem) => {
    setSelectedExperiment(experiment);
    setIsModalOpen(true);
  }, []);

  const handleStatusUpdate = React.useCallback(
    async (status: ExperimentStatus) => {
      if (!selectedExperiment) return;
      await setStatusUpdateAtom({ id: selectedExperiment.id, status });
    },
    [selectedExperiment, setStatusUpdateAtom]
  );

  const renderCell = React.useCallback(
    (experiment: ExperimentItem, columnKey: React.Key) => {
      switch (columnKey) {
        case 'name':
          return (
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <Link
                  to={`/experiments/${experiment.id}`}
                  className="text-primary hover:underline font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  {experiment.name}
                </Link>
                <span onClick={(e) => e.stopPropagation()}>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    className="min-w-6 w-6 h-6"
                    onPress={() => navigate(`/experiments/${experiment.id}`)}
                    aria-label="View experiment"
                  >
                    <RiEyeLine className="h-4 w-4" />
                  </Button>
                </span>
              </div>
              {experiment.description && (
                <span className="text-default-500 line-clamp-1 text-sm">{experiment.description}</span>
              )}
            </div>
          );
        case 'type':
          return <ExperimentTypesBadges experiment={experiment} />;
        case 'status':
          return (
            <Tooltip
              content={
                <div className="px-2 py-1 text-center text-xs">
                  Current status: {experiment.status.charAt(0).toUpperCase() + experiment.status.slice(1)}
                </div>
              }
              size="sm"
              placement="top"
            >
              <div className="flex flex-col gap-0.5">
                <Chip
                  color={getStatusColor(experiment.status)}
                  size="sm"
                  variant="flat"
                  startContent={getStatusIcon(experiment.status)}
                  className={`cursor-pointer ${['active', 'running'].includes(experiment.status) ? 'bg-success-100 text-success-700 border-success-300' : ''}`}
                  onClick={() => handleStatusClick(experiment)}
                >
                  {getStatusLabel(experiment.status)}
                </Chip>
                <span
                  className="text-default-400 cursor-pointer text-[10px]"
                  onClick={() => handleStatusClick(experiment)}
                >
                  Click to edit
                </span>
              </div>
            </Tooltip>
          );
        case 'domain':
          return (
            <DomainCell
              domain={{
                id: experiment.domain.id,
                domain: experiment.domain.domain,
                title: experiment.domain.title
              }}
              showTitle
              linked
              showID
            />
          );
        case 'organization':
          return (
            <OrganizationCell
              organization={{
                id: experiment.domain.organization.id,
                name: experiment.domain.organization.name,
                desc: null
              }}
              showDescription={false}
              dense
              linked
            />
          );
        case 'config_version':
          return (
            <div className="flex flex-col gap-1">
              {experiment.domain.cv && (
                <span className="text-default-600 font-mono text-xs">CV: {experiment.domain.cv}</span>
              )}
              {experiment.domain.ch && (
                <span className="text-default-400 font-mono text-[10px]">CH: {experiment.domain.ch}</span>
              )}
              {!experiment.domain.cv && !experiment.domain.ch && (
                <span className="text-default-400 text-xs italic">-</span>
              )}
            </div>
          );
        default:
          return null;
      }
    },
    [handleStatusClick, navigate]
  );

  const renderCollapseContent = React.useCallback(
    (experiment: ExperimentItem) => {
      return (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Experiment Details */}
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h4 className="text-default-700 flex items-center gap-2 text-sm font-semibold">
                  <div className="bg-primary h-2 w-2 rounded-full"></div>
                  Experiment Details
                </h4>
                {experiment.domain.unique_id && (
                  <Button
                    className="bg-primary/10 hover:bg-primary/20 h-6 cursor-pointer px-2 transition-colors"
                    color="primary"
                    size="sm"
                    variant="flat"
                    onPress={() => copyUniqueId(experiment.domain.unique_id, experiment.domain.domain)}
                    startContent={<RiFileCopyLine className="h-3 w-3" />}
                  >
                    <span className="font-mono text-xs">{experiment.domain.unique_id}</span>
                  </Button>
                )}
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex flex-col gap-1">
                  <span className="text-default-500 text-xs font-medium tracking-wide uppercase">Type</span>
                  <ExperimentTypesBadges experiment={experiment} />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-default-500 text-xs font-medium tracking-wide uppercase">Description</span>
                  <span className="text-default-700">{experiment.description || 'No description provided'}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-default-500 text-xs font-medium tracking-wide uppercase">Start Time</span>
                  <span className="text-default-700">
                    {experiment.start_time ? <DateTimeDisplay dateString={experiment.start_time} /> : 'Not scheduled'}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-default-500 text-xs font-medium tracking-wide uppercase">End Time</span>
                  <span className="text-default-700">
                    {experiment.end_time ? <DateTimeDisplay dateString={experiment.end_time} /> : 'Not scheduled'}
                  </span>
                </div>
              </div>
            </div>

            {/* User Information */}
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <h4 className="text-default-700 mb-4 flex items-center gap-2 text-sm font-semibold">
                <div className="bg-secondary h-2 w-2 rounded-full"></div>
                User Information
              </h4>
              <div className="border-default-200 overflow-hidden rounded-lg border">
                <table className="w-full">
                  <thead className="bg-default-100">
                    <tr>
                      <th className="text-default-600 px-3 py-2 text-left text-xs font-medium tracking-wide uppercase">
                        User
                      </th>
                      <th className="text-default-600 px-3 py-2 text-left text-xs font-medium tracking-wide uppercase">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-default-200 divide-y">
                    <tr>
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-default-500 text-xs font-medium tracking-wide uppercase">
                            Created By
                          </span>
                          <UserCell user={experiment.created_by} dense />
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-default-500 text-xs font-medium tracking-wide uppercase">
                            Created At
                          </span>
                          <DateTimeDisplay dateString={experiment.created_at} />
                        </div>
                      </td>
                    </tr>
                    {experiment.updated_by && (
                      <tr>
                        <td className="px-3 py-3">
                          <div className="flex flex-col gap-1">
                            <span className="text-default-500 text-xs font-medium tracking-wide uppercase">
                              Last Updated By
                            </span>
                            <UserCell user={experiment.updated_by} dense />
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex flex-col gap-1">
                            <span className="text-default-500 text-xs font-medium tracking-wide uppercase">
                              Updated At
                            </span>
                            <DateTimeDisplay dateString={experiment.updated_at} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Status & Moderation */}
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-default-700 flex items-center gap-2 text-sm font-semibold">
                  <div className={`h-2 w-2 rounded-full ${getStatusIndicatorColor(experiment.status)}`} />
                  Status: <span className="font-normal">{getStatusLabel(experiment.status)}</span>
                </h4>
                <div className="flex items-center gap-2">
                  {isBlockableStatus(experiment.status) && (
                    <PermissionGuard permission="Experiment.set_status">
                      <Button
                        color="danger"
                        size="sm"
                        variant="flat"
                        onPress={() => {
                          setSelectedExperiment(experiment);
                          setIsBlockModalOpen(true);
                        }}
                        startContent={<RiShieldCrossLine className="h-4 w-4" />}
                      >
                        Block
                      </Button>
                    </PermissionGuard>
                  )}
                  {experiment.status === 'blocked' && (
                    <PermissionGuard permission="Experiment.set_status">
                      <Button
                        color="success"
                        size="sm"
                        variant="flat"
                        onPress={() => {
                          setSelectedExperiment(experiment);
                          setIsUnblockModalOpen(true);
                        }}
                        startContent={<RiShieldCheckLine className="h-4 w-4" />}
                      >
                        Unblock
                      </Button>
                    </PermissionGuard>
                  )}
                  <PermissionGuard permission="Experiment.set_status">
                    <Button
                      color="default"
                      size="sm"
                      variant="light"
                      onPress={() => {
                        setSelectedExperiment(experiment);
                        setIsModalOpen(true);
                      }}
                      startContent={<RiEdit2Line className="h-4 w-4" />}
                    >
                      Edit Status
                    </Button>
                  </PermissionGuard>
                </div>
              </div>
            </div>

            {/* Configuration (Read-Only) */}
            {experiment.config && (
              <div className="rounded-lg bg-white p-4 shadow-sm">
                <div className="mb-4">
                  <h4 className="text-default-700 flex items-center gap-2 text-sm font-semibold">
                    <div className="bg-success h-2 w-2 rounded-full"></div>
                    Configuration
                  </h4>
                </div>
                <div className="space-y-4">
                  {/* Trackers */}
                  <div className="flex flex-col gap-2">
                    <span className="text-default-500 text-xs font-medium tracking-wide uppercase">
                      Active Trackers
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {experiment.config.trackers.map((tracker) => (
                        <Chip key={tracker} color="primary" size="sm" variant="flat" className="font-mono text-xs">
                          <span className="font-bold">{tracker}</span> ({getTrackerLabel(tracker)})
                        </Chip>
                      ))}
                    </div>
                  </div>

                  {/* Cookies */}
                  <div className="flex flex-col gap-2">
                    <span className="text-default-500 text-xs font-medium tracking-wide uppercase">Cookies</span>
                    <div className="flex flex-wrap gap-2">
                      {experiment.config.cookies.length > 0 ? (
                        experiment.config.cookies.map((cookie) => (
                          <Chip key={cookie} color="warning" size="sm" variant="flat" className="font-mono text-xs">
                            {cookie}
                          </Chip>
                        ))
                      ) : (
                        <span className="text-default-400 text-xs italic">No cookies configured</span>
                      )}
                    </div>
                  </div>

                  {/* Tracker Keys */}
                  {experiment.config.tracker_keys && experiment.config.tracker_keys.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <span className="text-default-500 text-xs font-medium tracking-wide uppercase">
                        Tracker Keys
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {experiment.config.tracker_keys.map((key) => (
                          <Chip key={key} color="primary" size="sm" variant="flat" className="font-mono text-xs">
                            {key}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Rules - Click */}
                  {experiment.config.rules?.click && experiment.config.rules.click.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <span className="text-default-500 text-xs font-medium tracking-wide uppercase">
                        Click Rules
                      </span>
                      <div className="flex flex-col gap-2">
                        {experiment.config.rules.click.map((rule, index) => (
                          <SelectorReadOnlyView
                            key={index}
                            rule={rule}
                            color="success"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Rules - Impression */}
                  {experiment.config.rules?.impression && experiment.config.rules.impression.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <span className="text-default-500 text-xs font-medium tracking-wide uppercase">
                        Impression Rules
                      </span>
                      <div className="flex flex-col gap-2">
                        {experiment.config.rules.impression.map((rule, index) => (
                          <SelectorReadOnlyView
                            key={index}
                            rule={rule}
                            color="secondary"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Stable Attributes */}
                  <div className="flex flex-col gap-2">
                    <span className="text-default-500 text-xs font-medium tracking-wide uppercase">
                      Stable Attributes
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {experiment.config.stable_attributes && experiment.config.stable_attributes.length > 0 ? (
                        experiment.config.stable_attributes.map((stable) => (
                          <Chip key={stable} color="secondary" size="sm" variant="flat" className="font-mono text-xs">
                            {stable}
                          </Chip>
                        ))
                      ) : (
                        <span className="text-default-400 text-xs italic">No stable attributes configured</span>
                      )}
                    </div>
                  </div>

                  {/* Campaign Params */}
                  {experiment.config.campaign_params && experiment.config.campaign_params.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <span className="text-default-500 text-xs font-medium tracking-wide uppercase">
                        Campaign Params
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {experiment.config.campaign_params.map((param) => (
                          <Chip key={param} color="danger" size="sm" variant="flat" className="font-mono text-xs">
                            {param}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Extra Settings (Read-Only) */}
            {experiment.extra && (
              <div className="rounded-lg bg-white p-4 shadow-sm">
                <div className="mb-4">
                  <h4 className="text-default-700 flex items-center gap-2 text-sm font-semibold">
                    <div className="bg-warning h-2 w-2 rounded-full"></div>
                    Extra Settings
                  </h4>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Chip
                    color={experiment.extra.force_click_tracking ? 'success' : 'default'}
                    size="sm"
                    variant={experiment.extra.force_click_tracking ? 'dot' : 'flat'}
                    className="font-medium"
                  >
                    Force Click Tracking
                  </Chip>
                  <Chip
                    color={experiment.extra.capture_all_properties ? 'success' : 'default'}
                    size="sm"
                    variant={experiment.extra.capture_all_properties ? 'dot' : 'flat'}
                    className="font-medium"
                  >
                    Capture All Properties
                  </Chip>
                  <Chip
                    color={experiment.extra.referrer_tracking ? 'success' : 'default'}
                    size="sm"
                    variant={experiment.extra.referrer_tracking ? 'dot' : 'flat'}
                    className="font-medium"
                  >
                    Referrer Tracking
                  </Chip>
                </div>
              </div>
            )}

            {/* Danger Zone */}
            <div className="rounded-lg border-2 border-danger-200 bg-danger-50 p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h4 className="text-danger-700 flex items-center gap-2 text-sm font-semibold">
                  <div className="bg-danger h-2 w-2 rounded-full"></div>
                  Danger Zone
                </h4>
                {isCancellableStatus(experiment.status) && (
                  <PermissionGuard permission="Experiment.set_status">
                    <Button
                      color="danger"
                      size="sm"
                      variant="flat"
                      onPress={() => {
                        setSelectedExperiment(experiment);
                        setIsCancelModalOpen(true);
                      }}
                      startContent={<RiCloseCircleLine className="h-4 w-4" />}
                    >
                      Cancel Experiment
                    </Button>
                  </PermissionGuard>
                )}
              </div>
              <p className="text-danger-600 text-xs">
                Cancelling an experiment halts its lifecycle and preserves analytical data. A reason is required.
              </p>
            </div>
          </div>
        </div>
      );
    },
    [
      setSelectedExperiment,
      setIsModalOpen,
      setIsBlockModalOpen,
      setIsUnblockModalOpen,
      setIsCancelModalOpen,
      copyUniqueId,
      getStatusIndicatorColor
    ]
  );

  const columns = React.useMemo(
    () => [
      { name: 'Name', uid: 'name', sortable: true, cellClass: 'whitespace-nowrap' },
      { name: 'Type', uid: 'type', sortable: true },
      { name: 'Status', uid: 'status', sortable: true },
      { name: 'Domain', uid: 'domain', sortable: true },
      { name: 'Workspace', uid: 'organization', sortable: true },
      { name: 'Config Version', uid: 'config_version', sortable: false, align: 'end', headClass: 'text-right' }
    ],
    []
  );

  // Columns visibility management
  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns);

  return (
    <>
      <TablePage
        columns={columns}
        currentPage={data?.meta?.current_page}
        data={sortedExperiments}
        emptyMessage="No experiments found"
        error={error}
        errorMessage="Failed to load experiments"
        filtersSection={
          <TableFilters
            hasActiveFilters={hasAnyFilter}
            isRefreshing={isLoading}
            onClearFilters={clearFilters}
            onRefresh={triggerRefresh}
          >
            <SearchInput
              placeholder="Search experiments..."
              value={searchTerm}
              onValueChange={setSearchTerm}
              onSearch={handleSearch}
              onClear={handleClearSearch}
            />

            <SearchInput
              placeholder="Search in config..."
              value={configSearchTerm}
              onValueChange={setConfigSearchTerm}
              onSearch={handleConfigSearch}
              onClear={handleClearConfigSearch}
            />

            <ColumnsToggleDropdown
              allColumns={columns.map((c) => ({ uid: c.uid, name: c.name }))}
              selected={visibleColumns}
              onChange={(next) => setVisibleColumns(next)}
            />

            <FilterSelect
              label="Status"
              options={[{ key: 'all', label: 'All' }, ...getStatusOptions()]}
              value={statusFilter}
              onChange={handleStatusChange}
            />

            <FilterMultiSelect
              label="Type"
              options={typeOptions}
              value={typesFilter}
              onChange={handleTypesChange}
            />
          </TableFilters>
        }
        headerColumns={headerColumns}
        isError={isError}
        isLoading={isLoading}
        renderCell={renderCell}
        sortDescriptor={sortDescriptor}
        title="Experiments"
        totalPages={data?.meta?.last_page}
        onPageChange={handlePageChange}
        onRefresh={triggerRefresh}
        onSortChange={(d) => setSortDescriptor(d)}
        enableCollapse={true}
        renderCollapseContent={renderCollapseContent}
      />

      {selectedExperiment && (
        <SetExperimentStatusModal
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
          experiment={selectedExperiment}
          onSubmit={handleStatusUpdate}
        />
      )}

      {selectedExperiment && (
        <BlockExperimentModal
          isOpen={isBlockModalOpen}
          onOpenChange={setIsBlockModalOpen}
          experiment={selectedExperiment}
          onSuccess={triggerRefresh}
        />
      )}

      {selectedExperiment && (
        <UnblockExperimentModal
          isOpen={isUnblockModalOpen}
          onOpenChange={setIsUnblockModalOpen}
          experiment={selectedExperiment}
          onSuccess={triggerRefresh}
        />
      )}

      {selectedExperiment && (
        <CancelExperimentModal
          isOpen={isCancelModalOpen}
          onOpenChange={setIsCancelModalOpen}
          experiment={selectedExperiment}
          onSuccess={triggerRefresh}
        />
      )}
    </>
  );
}
